import { agree, nounGrammar, type GrammarNoun } from "./tenant-settings/nounGrammar";
// Not "./tenant-settings/types": that module reaches back here through
// home-config → constants, and its VOCABULARY_DEFAULTS would still be unset.
import { vocabulary } from "./tenant-settings/vocabulary";

const VOCABULARY_DEFAULTS = vocabulary.defaults;

export const RESOURCES = [
  "members",
  "activities",
  // Avisar a base sobre uma experiencia nova (push, e-mail e aviso no app).
  // Recurso proprio, e nao `communications`: quem pode avisar sobre uma
  // experiencia publicada nao ganha o poder de mandar texto livre para todo o
  // clube. Nem `activities`: editar uma experiencia nao e falar com o publico.
  "activity_notices",
  // KLUB-64 (#252) — editorial curation of the home hero. Deliberately its own
  // resource: `settings` would make administering configuration authorise
  // curation, and `activities` would let whoever edits an activity reach into
  // the feed. Configuring the home's limits stays on `settings:update`; this
  // one authorises WHAT is featured, not HOW MANY (KTD13).
  "home",
  "feed",
  "benefits",
  "tiers",
  "bookings",
  "communications",
  "staff",
  "subscriptions",
  "audit",
  "settings",
  "legal",
  "interests",
  "notifications",
  "whatsapp",
  "referrals",
  "invites",
  "community",
  "groups",
  "challenges",
  "missions",
  "badges",
  "reports",
  // KLUB-144 (#362) — conectar a conta Stripe do clube. Recurso proprio, e nao
  // uma acao de `settings`: quem administra configuracao do clube nao e
  // necessariamente quem pode apontar para onde o dinheiro dos socios vai. A
  // separacao e R18.
  "payment_account",
  // KLUB-83 — conceder desconto com prazo na ficha. Recurso proprio, e nao
  // uma acao de `members` ou `subscriptions`: quem edita o cadastro ou ve a
  // assinatura nao e quem pode zerar uma mensalidade. A matriz so desenha
  // CRUD; acao extended some (licao KLUB-64). Super-admin cobre via `*`.
  "membership_discounts",
  // KLUB-205 — células que o produto já oferece como gesto e a matriz não
  // listava. `billing` / `payments` fantasmas nas policies RLS saem; Locais
  // deixa de andar atrás de `benefits`.
  "payments",
  // KLUB-58 — fila de estorno que a Stripe recusou para sempre. Recurso
  // proprio: ver Pagamentos (extrato) nao autoriza marcar que o dinheiro saiu.
  "blocked_refunds",
  "disputes",
  "operational_health",
  "venues",
] as const;

export type Resource = (typeof RESOURCES)[number];

export const CRUD_ACTIONS = ["view", "create", "update", "delete"] as const;
export type CrudAction = (typeof CRUD_ACTIONS)[number];

/** Resource-scoped actions beyond CRUD. */
export const EXTENDED_ACTIONS = ["invite", "revoke", "cancel", "issue_contractor", "export_data"] as const;
export type ExtendedAction = (typeof EXTENDED_ACTIONS)[number];

/** Any action that can appear in RESOURCE_ACTIONS[*]. */
export type ResourceAction = CrudAction | ExtendedAction;

export const SPECIAL_ACTIONS = ["checkin"] as const;
export type SpecialAction = (typeof SPECIAL_ACTIONS)[number];

/** Which actions each resource supports */
export const RESOURCE_ACTIONS: Record<Resource, readonly ResourceAction[]> = {
  // export_data is required by supabase/functions/export-member-data and has
  // existed in production staff_roles all along, but was missing from this
  // catalog. The permission editor derives everything it renders from here, so
  // its absence made three separate operator gestures wipe the grant without a
  // word: unchecking "Ver" on Membros, the select-all shortcut on the row, and
  // the select-all on the "Ver" column. Listing it is the fix for all three.
  members: [...CRUD_ACTIONS, "export_data"] as const,
  activities: CRUD_ACTIONS,
  // There is nothing to create or delete: the hero is a curated list over
  // entities that already exist. `view` opens the Destaques screen, `update`
  // is what the reorder op gates on.
  home: ["view", "update"],
  feed: CRUD_ACTIONS,
  benefits: CRUD_ACTIONS,
  tiers: CRUD_ACTIONS,
  bookings: CRUD_ACTIONS,
  communications: CRUD_ACTIONS,
  staff: [...CRUD_ACTIONS, "revoke"] as const,
  subscriptions: [...CRUD_ACTIONS, "invite", "cancel"] as const,
  audit: ["view"],
  settings: ["view", "update"],
  legal: ["view", "update"],
  interests: CRUD_ACTIONS,
  notifications: ["view", "update"],
  whatsapp: ["view", "update"],
  referrals: CRUD_ACTIONS,
  invites: ["view", "cancel", "issue_contractor"],
  community: ["view"],
  groups: CRUD_ACTIONS,
  challenges: CRUD_ACTIONS,
  missions: CRUD_ACTIONS,
  badges: CRUD_ACTIONS,
  reports: ["view", "update"],
  // Nao ha o que criar nem excluir: a conta e uma so, e substitui-la e um
  // `update`. `view` abre a aba e mostra o estado; `update` e o que conectar e
  // substituir exigem.
  payment_account: ["view", "update"],
  // So o envio: o aviso nao tem tela propria para `view`, e uma vez enviado
  // nao se edita nem se apaga.
  activity_notices: ["create"],
  // Nao ha o que criar nem excluir: um grant ativo por pessoa, substituir e
  // `update`, revogar tambem. `view` mostra o desconto na ficha; `update` e o
  // que conceder, substituir e revogar exigem.
  membership_discounts: ["view", "update"],
  payments: ["view"],
  blocked_refunds: ["view", "update"],
  disputes: ["view"],
  operational_health: ["view", "update"],
  venues: CRUD_ACTIONS,
};

/** Structured permissions stored in staff_roles.permissions JSONB */
export interface StructuredPermissions {
  resources: Partial<Record<Resource, ResourceAction[]>>;
  actions: SpecialAction[];
}

/** Fallback labels when tenant vocabulary is not available.
 *  PermissionMatrix prefers `vocab.*.plural` for nouns the tenant owns. */
export const RESOURCE_LABELS: Record<Resource, string> = {
  members: "Pessoas",
  activities: "Experiências",
  activity_notices: "Aviso de nova experiência",
  home: "Destaque",
  feed: "News",
  benefits: "Benefícios",
  tiers: "Planos",
  bookings: "Reservas",
  communications: "Comunicações",
  staff: "Usuários",
  subscriptions: "Assinaturas",
  audit: "Auditoria",
  settings: "Configurações",
  legal: "LGPD",
  interests: "Interesses",
  notifications: "Notificações",
  whatsapp: "Comunicação",
  referrals: "Indicações",
  invites: "Convites",
  community: "Comunidade",
  groups: "Grupos",
  challenges: "Desafios",
  missions: "Missões",
  badges: "Conquistas",
  reports: "Denúncias",
  payment_account: "Conta Stripe",
  membership_discounts: "Descontos",
  payments: "Pagamentos",
  blocked_refunds: "Estornos bloqueados",
  disputes: "Disputas",
  operational_health: "Saúde operacional",
  venues: "Locais",
};

/** Resources that still exist in the catalog/RLS but have no admin gesture. */
export const MATRIX_HIDDEN_RESOURCES = [
  "audit",
  "community",
  "notifications",
] as const satisfies readonly Resource[];

export interface MatrixSection {
  title: string | null;
  resources: readonly Resource[];
}

export const MATRIX_SECTIONS: readonly MatrixSection[] = [
  {
    title: null,
    resources: [
      "members",
      "subscriptions",
      "membership_discounts",
      "invites",
      "disputes",
      "staff",
      "tiers",
      "venues",
      "bookings",
      "interests",
      "feed",
      "activities",
      "activity_notices",
      "benefits",
      "home",
      "communications",
      "groups",
      "challenges",
      "missions",
      "badges",
      "reports",
      "payments",
      "blocked_refunds",
      "referrals",
      "operational_health",
    ],
  },
  {
    title: "Configurações",
    resources: ["settings", "whatsapp", "payment_account", "legal"],
  },
];

/** Short help for the permission matrix: what the resource is, and where it gates. */
export interface ResourceHelp {
  summary: string;
  usedIn: readonly string[];
}

/** Help for the resources whose copy names no club noun. */
const STATIC_RESOURCE_HELP: Omit<Record<Resource, ResourceHelp>, VocabHelpResource> = {
  activities: {
    summary: "Criar, editar e cancelar as experiências do clube.",
    usedIn: ["menu de experiências", "detalhe"],
  },
  home: {
    summary: "O que entra em Destaque no app. Quantos itens cabem na home é Configurações.",
    usedIn: ["Destaque"],
  },
  feed: {
    summary: "Posts e novidades do News.",
    usedIn: ["News"],
  },
  benefits: {
    summary: "Benefícios oferecidos às pessoas. Independente de Locais.",
    usedIn: ["Benefícios"],
  },
  bookings: {
    summary: "Reservas de espaços.",
    usedIn: ["Reservas", "app do staff"],
  },
  communications: {
    summary: "Comunicados em massa. Distinto de Comunicação (WhatsApp em Configurações).",
    usedIn: ["Comunicações"],
  },
  staff: {
    summary: "Equipe: cadastrar, editar ficha e revogar. Desenhar o cargo continua só com Super Admin.",
    usedIn: ["Equipe · Usuários"],
  },
  audit: {
    summary: "Log de auditoria. Só existe no platform, não no admin do clube.",
    usedIn: ["platform"],
  },
  settings: {
    summary: "A tela Configurações do clube: convites, reservas, check-in, home screen, indicação e popup. Não é o cadastro do clube no platform.",
    usedIn: ["Configurações"],
  },
  legal: {
    summary: "Documentos LGPD e políticas.",
    usedIn: ["Configurações · LGPD"],
  },
  interests: {
    summary: "Interesses usados para afinidade e audiência.",
    usedIn: ["Interesses"],
  },
  notifications: {
    summary: "Não há tela própria no admin.",
    usedIn: [],
  },
  whatsapp: {
    summary: "Aba Comunicação em Configurações: número de WhatsApp e mensagens do clube. Distinto de Comunicações (comunicados).",
    usedIn: ["Configurações · Comunicação"],
  },
  referrals: {
    summary: "Indicações entre pessoas e recompensas ligadas a elas.",
    usedIn: ["Indicações", "Recompensas"],
  },
  community: {
    summary: "Não há tela própria no admin. Grupos, desafios, trilhas e denúncias têm recurso cada um.",
    usedIn: [],
  },
  groups: {
    summary: "Grupos da comunidade.",
    usedIn: ["Grupos"],
  },
  challenges: {
    summary: "Desafios da comunidade.",
    usedIn: ["Desafios"],
  },
  missions: {
    summary: "Trilhas da comunidade.",
    usedIn: ["Trilhas"],
  },
  badges: {
    summary: "Conquistas.",
    usedIn: ["Conquistas", "ficha"],
  },
  reports: {
    summary: "Denúncias da comunidade.",
    usedIn: ["Denúncias"],
  },
  activity_notices: {
    summary: "Avisar o público de uma experiência publicada (push, e-mail e aviso no app) e os confirmados quando a galeria sai. Não libera Comunicações.",
    usedIn: ["Experiência · Notificação", "Experiência · Fotos"],
  },
  payment_account: {
    summary: "Conectar a conta Stripe do clube — para onde o dinheiro vai. Distinto de Pagamentos (extrato das cobranças).",
    usedIn: ["Configurações · Conta Stripe"],
  },
  membership_discounts: {
    summary: "Conceder ou revogar desconto com prazo na ficha. Quem vê Assinaturas não ganha isto automaticamente.",
    usedIn: ["ficha · Desconto"],
  },
  payments: {
    summary: "Extrato das cobranças das pessoas. Distinto de Conta Stripe (onde o clube recebe) e de Disputas.",
    usedIn: ["Pagamentos"],
  },
  blocked_refunds: {
    summary: "Estorno que a Stripe recusou para sempre. Ver Pagamentos não autoriza marcar que o dinheiro saiu.",
    usedIn: ["Estornos bloqueados"],
  },
  disputes: {
    summary: "Chargeback e consulta do banco na ficha da pessoa.",
    usedIn: ["ficha · disputa"],
  },
  operational_health: {
    summary: "Falhas de notificação e resolução operacional.",
    usedIn: ["Saúde operacional"],
  },
  venues: {
    summary: "Espaços físicos do clube. Independente de Benefícios.",
    usedIn: ["Locais"],
  },
};

/** Resources whose help names member, prospect or tier (KLUB-283). */
type VocabHelpResource = "members" | "tiers" | "subscriptions" | "invites";

/** The nouns the vocabulary-driven help reads, structurally. */
export interface ResourceHelpVocabulary {
  member: GrammarNoun;
  prospect: GrammarNoun;
  tier: GrammarNoun;
}

/**
 * Help for every resource, naming member, prospect and tier with the club's
 * terms. `usedIn` keeps naming the screen the admin shows ("Planos" is the
 * sidebar entry, which reads the tier plural).
 */
export function resourceHelpFor(vocab: ResourceHelpVocabulary): Record<Resource, ResourceHelp> {
  const member = nounGrammar(vocab.member, VOCABULARY_DEFAULTS.member.gender);
  const prospect = nounGrammar(vocab.prospect, VOCABULARY_DEFAULTS.prospect.gender);
  const tier = nounGrammar(vocab.tier, VOCABULARY_DEFAULTS.tier.gender);
  const tierInvite = `convite de ${tier.singularLower}`;
  return {
    ...STATIC_RESOURCE_HELP,
    members: {
      summary: `Lista e ficha de Pessoas — ${prospect.pluralLower} e ${member.pluralLower}. Exportar dados entra aqui.`,
      usedIn: ["Pessoas", "ficha", "exportar dados"],
    },
    tiers: {
      summary: `${tier.pluralCap} do clube: preços, vitrine e regras.`,
      usedIn: [vocab.tier.plural],
    },
    subscriptions: {
      summary: `${tier.singularCap} ${agree(tier, "pago", "paga", tier.gender)} da pessoa: ver status, convidar para ${tier.a} e Encerrar. Conceder desconto na ficha é o recurso Descontos, ao lado.`,
      usedIn: ["ficha", "Encerrar", tierInvite],
    },
    invites: {
      summary: `Cancelar ${tierInvite} e emitir magic link de contratante.`,
      usedIn: [tierInvite, "emitir contratante"],
    },
  };
}

/** Help with the platform default vocabulary, for callers without a club. */
export const RESOURCE_HELP: Record<Resource, ResourceHelp> = resourceHelpFor(VOCABULARY_DEFAULTS);

export const CRUD_ACTION_LABELS: Record<CrudAction, string> = {
  view: "Ver",
  create: "Criar",
  update: "Editar",
  delete: "Excluir",
};

export const EXTENDED_ACTION_LABELS: Record<ExtendedAction, string> = {
  invite: "Convidar",
  revoke: "Revogar",
  // Encerrar is the product copy on the member sheet (KLUB-205 AE1).
  cancel: "Encerrar",
  issue_contractor: "Emitir contratante",
  export_data: "Exportar dados",
};

/** Union label map covering both CRUD and extended actions. */
export const RESOURCE_ACTION_LABELS: Record<ResourceAction, string> = {
  ...CRUD_ACTION_LABELS,
  ...EXTENDED_ACTION_LABELS,
};

export const SPECIAL_ACTION_LABELS: Record<SpecialAction, string> = {
  checkin: "Check-in e QR",
};

/**
 * Check if a structured permissions object grants a specific resource action.
 * Handles wildcard ("*") in resources keys.
 */
export function checkResourcePermission(
  permissions: StructuredPermissions | null | undefined,
  resource: Resource,
  action: ResourceAction,
): boolean {
  if (!permissions) return false;
  // Wildcard grants everything
  if (permissions.resources["*" as Resource]) return true;
  const actions = permissions.resources[resource];
  if (!actions) return false;
  return actions.includes(action);
}

/**
 * Check if a structured permissions object grants a special action.
 */
export function checkSpecialAction(
  permissions: StructuredPermissions | null | undefined,
  action: SpecialAction,
): boolean {
  if (!permissions) return false;
  if (permissions.resources["*" as Resource]) return true;
  return permissions.actions.includes(action);
}

/**
 * Check if permissions grant wildcard (full access).
 */
export function isWildcard(
  permissions: StructuredPermissions | null | undefined,
): boolean {
  if (!permissions) return false;
  return !!(permissions.resources as Record<string, unknown>)["*"];
}

/** Empty permissions object */
export const EMPTY_PERMISSIONS: StructuredPermissions = {
  resources: {},
  actions: [],
};

/** Wildcard permissions object */
export const WILDCARD_PERMISSIONS: StructuredPermissions = {
  resources: { "*": [] } as unknown as StructuredPermissions["resources"],
  actions: [],
};
